/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useRef, useState } from "react"
import { MultiButton } from "./MultiButton"
import { Breadcrumb, Grid, Helmet, Icon, Spacer, useMediaQuery } from "@cd2/components"
import { NavLink } from "react-router-dom"
import { useTheme } from "@cd2/theme"
import { Title } from "./Title"

import { IComponentProp, IComponentProps } from "../types/utils"
import { FloatingActionsPlacement } from "./FloatingActions"
import { LockBanner } from "./LockBanner"
import { Flex } from "./Flex"

export type ICrumb = {
  name: string
  icon?: IComponentProp<typeof Icon, "icon">
  to: string
}

interface IProps {
  title: string | React.ReactNode
  pre?: string
  subtitle?: React.ReactNode
  breadcrumbs?: ICrumb[]
  actions?: IComponentProp<typeof MultiButton, "buttons">
  moreActions?: IComponentProp<typeof MultiButton, "buttons">
  children: React.ReactNode
  lockBannerProps?: IComponentProps<typeof LockBanner>
  innerClassName?: string
  // New props
  newLayout?: boolean // force new layout, this will always be true if filters are passed
  filters?: React.ReactNode
  afterActions?: React.ReactNode
  noPadding?: boolean
}

export function PageWrapper({
  title,
  pre,
  subtitle,
  breadcrumbs = [],
  actions,
  moreActions,
  children,
  lockBannerProps,
  innerClassName,
  newLayout,
  filters,
  afterActions,
  noPadding,
}: IProps) {
  const { themeColor } = useTheme()

  const ref = useRef<HTMLDivElement>(null)

  const isLarge = useMediaQuery({ minWidth: 768 }, { ref })

  const [available, setAvailable] = useState(true)

  let columns = ["auto" as const, "max-content" as const]

  if (!isLarge) {
    columns = ["auto"]
  }

  if (filters) newLayout = true
  if (afterActions) newLayout = true

  return (
    <div
      ref={ref}
      css={css`
        position: relative;
      `}
    >
      {typeof title === "string" && <Helmet title={`${title} | CleverGoose`} />}
      {lockBannerProps && (
        <LockBanner
          {...lockBannerProps}
          onLockChange={locked => {
            setAvailable(!locked)
          }}
        />
      )}
      <div
        css={css`
          position: relative;
          z-index: 4;
          pointer-events: ${available ? "all" : "none"};
        `}
      >
        <div
          css={css`
            padding: ${noPadding ? 0 : "16px 16px 120px"};
            margin: 0 auto;

            @media (min-width: 768px) {
              padding: ${noPadding ? 0 : "32px 32px 200px"};
            }
          `}
          className={innerClassName}
        >
          {breadcrumbs.length > 0 && (
            <div>
              <Breadcrumb>
                {breadcrumbs.map(crumb => {
                  const { icon, name, to } = crumb
                  return (
                    <NavLink
                      key={name}
                      to={to}
                      css={css`
                        color: #999;
                        text-decoration: none;

                        &.active {
                          color: ${themeColor("primary")};
                        }
                      `}
                      exact
                    >
                      {icon && (
                        <div
                          css={css`
                            display: inline-block;
                            width: 16px;
                            vertical-align: baseline;
                            position: relative;
                            margin-right: 2px;
                          `}
                        >
                          <Icon icon={icon} />
                        </div>
                      )}{" "}
                      {name}
                    </NavLink>
                  )
                })}
              </Breadcrumb>
            </div>
          )}
          {breadcrumbs.length > 0 && <Spacer size={newLayout ? 16 : 32} />}
          <Grid columns={columns} gap={8} alignItems="end">
            <Flex direction="column">
              <Title pre={pre} title={title} prompt={subtitle} />
              {newLayout && (
                <React.Fragment>
                  <Spacer />
                  <Grid gap={16}>
                    {actions && <MultiButton buttons={actions} />}
                    {moreActions && moreActions?.length > 0 && <Spacer />}
                    {moreActions && moreActions?.length > 0 && (
                      <MultiButton buttons={moreActions} />
                    )}
                  </Grid>
                </React.Fragment>
              )}
              {afterActions && (
                <React.Fragment>
                  <Flex.Spacer />
                  <Spacer />
                  {afterActions}
                </React.Fragment>
              )}
            </Flex>
            {!newLayout && (
              <div>
                {actions && <MultiButton buttons={actions} justifyContent={"flex-end"} />}
                {moreActions && moreActions?.length > 0 && actions && <Spacer />}
                {moreActions && moreActions?.length > 0 && (
                  <MultiButton buttons={moreActions} justifyContent={"flex-end"} />
                )}
              </div>
            )}
            {newLayout && filters}
          </Grid>
          {newLayout && <Spacer />}
          {/* THIS KEY IS ESSENTIAL TO MAKE FORM2 RELOAD WHEN INITIAL STATE CHANGES ON LOCK/UNLOCK CHANGE */}
          <React.Fragment key={`${available}`}>{children}</React.Fragment>
        </div>
        <FloatingActionsPlacement />
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs = [],
}: {
  title: string
  subtitle?: string
  breadcrumbs?: ICrumb[]
}) {
  const theme = useTheme()
  return (
    <div>
      <Helmet title={`${title} | CleverGoose`} />
      {breadcrumbs.length > 0 && (
        <div>
          <Breadcrumb>
            {breadcrumbs.map(crumb => {
              const { icon, name, to } = crumb
              return (
                <NavLink
                  key={name}
                  to={to}
                  css={css`
                    color: #999;
                    text-decoration: none;

                    &.active {
                      color: ${theme.themeColor("primary")};
                    }
                  `}
                  exact
                >
                  {icon && (
                    <div
                      css={css`
                        display: inline-block;
                        width: 16px;
                        vertical-align: baseline;
                        position: relative;
                        top: 2px;
                        margin-right: 2px;
                      `}
                    >
                      <Icon icon={icon} />
                    </div>
                  )}{" "}
                  {name}
                </NavLink>
              )
            })}
          </Breadcrumb>
        </div>
      )}
      {breadcrumbs.length > 0 && <Spacer size={32} />}
      <Title title={title} prompt={subtitle} />
    </div>
  )
}

export function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        padding: 16px 16px 120px;
        margin: 0 auto;

        @media (min-width: 768px) {
          padding: 32px 32px 200px;
        }
      `}
    >
      {children}
    </div>
  )
}
